/**
 * One vocabulary and one copy set for "you can't see this case" (MDRS-343).
 *
 * Opening a case the backend refuses used to paint the full case shell — tab bar,
 * action buttons, an empty Assessment Prep and a breadcrumb reading literally
 * "Examinee Name:  - Claim: " — because nothing in the shell looked at the failed
 * fetch. It read as a broken page rather than a closed door.
 *
 * The backend has two distinct refusals for a case URL, and they mean different
 * things to the person reading the screen:
 *
 * - **403** (`api/cases/handler.py` → `can_read_case`) — the case is real and the
 *   viewer is not on it.
 * - **404** (`utils/case_queries.py` → `enforce_doctor_ready`) — either no such
 *   case, or a case that exists but hasn't been released to this doctor. MDRS-230
 *   returns 404 for both *on purpose*, so the response can't be used to confirm a
 *   case is there.
 *
 * That second one is why the copy is split rather than sharing one "no access"
 * message: telling a doctor they "don't have permission" to a case that answered
 * 404 would leak exactly the fact the 404 is hiding. Keep the wording for
 * `UNAVAILABLE` free of permission language.
 */

export const CASE_ACCESS_STATE = {
  /** The case loaded, or is still loading — render the real shell. */
  OK: "ok",
  /** 403 — the case exists and belongs to someone else. */
  FORBIDDEN: "forbidden",
  /** 404 — no such case, or not released to this doctor. Deliberately vague. */
  UNAVAILABLE: "unavailable",
  /** 5xx, network failure, anything unclassified. Retrying may fix it. */
  ERROR: "error",
} as const;

export type CaseAccessState =
  (typeof CASE_ACCESS_STATE)[keyof typeof CASE_ACCESS_STATE];

/** The states that replace the case shell with a notice. */
export type CaseAccessProblem = Exclude<
  CaseAccessState,
  typeof CASE_ACCESS_STATE.OK
>;

/**
 * Classify a failed case fetch by its HTTP status.
 *
 * Only 403 and 404 carry a meaning worth spelling out. Everything else —
 * including a 4xx we don't expect and a missing status (the request threw before
 * a response, e.g. offline) — is the retryable error state; guessing at a
 * permission story for an unknown status would be worse than saying "try again".
 */
export const caseAccessStateForStatus = (status?: number): CaseAccessState => {
  if (status === 403) return CASE_ACCESS_STATE.FORBIDDEN;
  if (status === 404) return CASE_ACCESS_STATE.UNAVAILABLE;
  return CASE_ACCESS_STATE.ERROR;
};

type CaseAccessCopy = {
  title: string;
  body: string;
  /**
   * Terminal breadcrumb label for this state.
   *
   * Not decoration. `ui/breadcrumb`'s `Breadcrumbs` renders the LAST item as
   * `BreadcrumbPage` — a `<span role="link" aria-disabled="true"
   * aria-current="page">` — so a lone "Report Studio" crumb is a dead label that
   * also claims to be the current page. Adding this second crumb is what turns
   * the first one back into a real anchor and moves `aria-current` onto
   * something true.
   */
  crumb: string;
  /** Whether a retry can plausibly change the answer. */
  canRetry: boolean;
};

export const CASE_ACCESS_COPY: Record<CaseAccessProblem, CaseAccessCopy> = {
  [CASE_ACCESS_STATE.FORBIDDEN]: {
    title: "You don't have access to this case",
    body: "This case belongs to someone else. If you need to work on it, ask MEDirect to assign it to you.",
    crumb: "No access",
    canRetry: false,
  },
  [CASE_ACCESS_STATE.UNAVAILABLE]: {
    title: "This case isn't available",
    // No permission language here, and nothing that confirms the case exists —
    // see the module comment.
    body: "It may have been removed, or it may not be ready for you yet. Your case list shows everything you can open right now.",
    crumb: "Not available",
    canRetry: false,
  },
  [CASE_ACCESS_STATE.ERROR]: {
    title: "We couldn't load this case",
    body: "Something went wrong at our end. Try again — if it keeps happening, let MEDirect know.",
    crumb: "Couldn't load",
    canRetry: true,
  },
};

export const CASE_ACCESS_ACTIONS = {
  retry: "Try again",
  back: "Back to Report Studio",
} as const;
